import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppConstant } from '../../../../../../../Assests/AppConstant';
import { sagaActions } from '../../../../../../../../sagas/sagaActions';
import { getReleaseBannerAdminSelector } from '../../../../../../../../redux/reducers/reducerSlices/ReleaseBannerAdminReducer';
import { getUserSelector } from '../../../../../../../../redux/reducers/reducerSlices/UserReducer';
import DrilldownTable from '../../../../Dashboard/DefaultDashboard/DrillDownReports/Common/DrilldownTable';
import CommonToggleSwitch from '../../../../../../Common/CommonToggleSwitch/CommonToggleSwitch';
import Popup from '../../../../../../Common/Popup/Popup';
import EditIcon from '../../../../../../../Assests/Images/edit-icon-new.png';
import CustomButtonSS from '../../../../../../Common/CustomButton/CustomButtonSS';

/** Max characters before text is truncated with ellipsis in table cells */
const MAX_CELL_CHARS = 30;

const ReleaseBannerList = (props) => {

    const {
        themeSelected,
        allowPermissionE,
        allowPermissionD,
        editBannerAction,
        changeActiveStatus,
        changeDeletedStatus,
    } = props;

    const releaseBannerAdminState = useSelector(getReleaseBannerAdminSelector);
    const { bannerAdminList, initateLoading } = releaseBannerAdminState;

    const user = useSelector(getUserSelector);
    const { arrangeOrder, arrangeKey } = user;

    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pendingDeleteItem, setPendingDeleteItem] = useState(null);

    const { tableHeader } = AppConstant.superUserAdmin.releaseBannerAdmin;

    /**
     * New column layout:
     * S.No | Notification Type | App Type | Details | Start Date & Time | End Date & Time | Active | Actions
     */
    const bannerTitleDD = [
        { id: 'serial_num',       name: tableHeader.serialNum },
        { id: 'notificationType', name: tableHeader.notificationType },
        { id: 'service',          name: tableHeader.service },
        { id: 'details',          name: tableHeader.details },
        { id: 'startDateTime',    name: tableHeader.startDateTime },
        { id: 'endDateTime',      name: tableHeader.endDateTime },
        { id: 'isActive',         name: tableHeader.isActive },
        { id: 'actions',          name: tableHeader.actions },
    ];

    // ─── Helpers ─────────────────────────────────────────────────────────────

    /**
     * Renders text truncated to MAX_CELL_CHARS with an ellipsis.
     * Full text is shown via the native `title` tooltip on hover.
     */
    const truncatedCell = (text) => {
        if (!text && text !== 0) return '—';
        const str = String(text);
        if (str.length <= MAX_CELL_CHARS) return str;
        return (
            <span
                title={str}
                style={{
                    display: 'inline-block',
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                    cursor: 'default',
                }}
            >
                {str}
            </span>
        );
    };

    /**
     * Details column:
     *   - Release type  → "Prev: X → Curr: Y"
     *   - Notification  → banner heading or '—'
     */
    const detailsCell = (el) => {
        const type = el.notificationType || 'Release';
        if (type === 'Release') {
            const prev = el.prevVersion || '—';
            const curr = el.currentVersion || '—';
            return truncatedCell(`${prev} → ${curr}`);
        }
        return truncatedCell(el.bannerHeading || '—');
    };

    /**
     * Start Date & Time column:
     *   - Release type  → releaseDate
     *   - Notification  → startDateTime
     */
    const startDateCell = (el) => {
        const type = el.notificationType || 'Release';
        if (type === 'Release') {
            return truncatedCell(el.releaseDate || '—');
        }
        return truncatedCell(el.startDateTime || '—');
    };

    /**
     * End Date & Time column:
     *   - Release type  → '—'
     *   - Notification  → endDateTime
     */
    const endDateCell = (el) => {
        const type = el.notificationType || 'Release';
        if (type === 'Release') {
            return '—';
        }
        return truncatedCell(el.endDateTime || '—');
    };

    /**
     * Toggle switch for isActive — centered wrapper ensures proper cell alignment.
     */
    const activeToggle = (el) => (
        <div className='d-flex justify-content-center align-items-center'>
            <CommonToggleSwitch
                value={!!el.isActive}
                title={''}
                onChangeValue={() => changeActiveStatus && changeActiveStatus(el)}
                allowPermissionD={allowPermissionD}
            />
        </div>
    );

    /** Edit + Delete actions */
    const actionsButton = (el) => (
        <div className='m-0 p-0 d-flex justify-content-center align-items-center gap-2'>
            {allowPermissionE && (
                <img
                    src={EditIcon}
                    alt='Edit'
                    title='Edit'
                    style={{ width: 20, height: 20, cursor: 'pointer', objectFit: 'contain' }}
                    onClick={() => editBannerAction && editBannerAction(el)}
                />
            )}
            {allowPermissionD && (
                <CustomButtonSS
                    disabled={false}
                    isLoading={false}
                    className='no-border no-bg'
                    onClick={() => {
                        setPendingDeleteItem(el);
                        setShowDeleteConfirm(true);
                    }}
                    isIcon={true}
                    iconClass='fa-regular fa-trash-can fa-lg'
                />
            )}
        </div>
    );

    const formatListDataDrillDown = (data) => {
        return data.map((el, index) => ({
            id: el.id,
            0: index + 1,
            1: truncatedCell(el.notificationType || '—'),
            2: truncatedCell(el.service),
            3: detailsCell(el),
            4: startDateCell(el),
            5: endDateCell(el),
            6: activeToggle(el),
            7: actionsButton(el),
        }));
    };

    // ─── Delete confirmation popup ────────────────────────────────────────────
    const handleDeleteConfirm = (text) => {
        if (text === AppConstant.commonStrings.yes) {
            changeDeletedStatus && changeDeletedStatus(pendingDeleteItem);
        }
        setShowDeleteConfirm(false);
        setPendingDeleteItem(null);
    };

    const deleteConfirmPopup = () => {
        const popupObj = {
            id: 'releaseBannerDeleteConfirm',
            modalLabel: 'releaseBannerDeleteConfirm',
            showClose: true,
            showfooter: true,
            showHeader: true,
            sendDriverNotification: false,
            modalSize: 'md',
            title: 'Confirm Action',
            bodyText: 'Are you sure you want to delete this banner?',
            subText: '',
            keyboard: false,
            backdrop: 'static',
            footerBtn: {
                btn1: { show: true, text: AppConstant.commonStrings.yes },
                btn2: { show: true, text: AppConstant.commonStrings.no },
            },
        };
        return (
            <Popup
                {...popupObj}
                popupBtnHandler={(text) => handleDeleteConfirm(text)}
                closepopup={() => {
                    setShowDeleteConfirm(false);
                    setPendingDeleteItem(null);
                }}
                themeSelected={themeSelected}
                showModal={showDeleteConfirm}
            />
        );
    };

    return (
        <>
            {deleteConfirmPopup()}
            <DrilldownTable
                themeSelected={themeSelected}
                bodyData={
                    bannerAdminList && bannerAdminList.length
                        ? formatListDataDrillDown(bannerAdminList)
                        : []
                }
                titleData={bannerTitleDD}
                showCollapse={false}
                showInput={false}
                scroll={true}
                showSort={true}
                arrangeOrder={arrangeOrder}
                arrangeKey={arrangeKey}
                drillDownReport={bannerAdminList}
                initiateDrillDownReport={initateLoading}
                loaderArray={10}
                sortingAction={sagaActions.SORT_USER_TABLE}
                subRoot={{ pathName: 'releaseBannerAdmin' }}
                showUTCTime={false}
            />
        </>
    );
};

export default ReleaseBannerList;
